import { Modal } from 'antd';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { apiClient } from '@/api/client';
import { extractApiError } from '@/originator/statusUtils';

import { isDevLoginEnabled } from './devLogin';
import { subscribeToSessionActivity } from './sessionTimer';
import { invalidateSession } from './useSession';

/** Warn this many seconds before the absolute session cap, not on every refresh tick. */
const WARNING_THRESHOLD_SECONDS = 5 * 60;
/** Fallback only — normally overridden by the server-provided refreshIntervalSeconds. */
const DEFAULT_REFRESH_INTERVAL_SECONDS = 15 * 60;
/** How often to check the absolute deadline against the warning threshold. */
const DEADLINE_CHECK_MS = 30_000;

type RefreshResponse = {
  accessTokenExpiresAt: number;
  sessionExpiresAt: number;
  refreshIntervalSeconds: number;
};

export type SessionTimerSeed = {
  accessTokenExpiresAt: number;
  sessionExpiresAt: number;
  refreshIntervalSeconds: number;
};

/**
 * Drives the BFF session-refresh cadence for ISAM/Entra sessions:
 *   - Calls POST /auth/refresh on a server-driven interval (default 15 min — see
 *     AuthOptions.RefreshIntervalMinutes) so the backend's stored ISAM access token
 *     stays current.
 *   - Resets that interval and refreshes immediately whenever notifySessionActivity()
 *     fires (wired into the upload/save/submit call sites — see sessionTimer.ts).
 *   - Warns the user (sticky modal, not a dismissible toast — losing unsaved advisor
 *     work to a silent logout would be worse than an extra click) a few minutes before
 *     the 5-hour absolute session cap, which activity can NOT extend.
 *   - Redirects to login through the same invalidateSession() → POST /auth/logout →
 *     navigate sequence LogoutPage.tsx already uses, once the session is confirmed gone.
 *
 * No token is ever read from or written to the response — only expiry timestamps, per
 * the BFF's "browser never sees a token" principle (see AuthEndpoints.cs).
 *
 * Pass `null` before the session has resolved; the effect no-ops until a seed arrives.
 * Skips entirely under Dev Login, which has no real backend TTL to refresh against.
 */
export function useSessionTimer(seed: SessionTimerSeed | null): void {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const warnedRef = useRef(false);
  const sessionExpiresAtRef = useRef<number | null>(null);
  const modalHandleRef = useRef<{ destroy: () => void } | null>(null);

  const redirectToLogin = useCallback(() => {
    modalHandleRef.current?.destroy();
    invalidateSession();
    apiClient
      .post('/auth/logout', null, { baseURL: '/' })
      .catch(() => {
        /* best-effort — proceed to the login redirect regardless */
      })
      .finally(() => {
        navigate('/auth/logged-out', { replace: true });
      });
  }, [navigate]);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const res = await apiClient.post<RefreshResponse>('/auth/refresh', null, { baseURL: '/' });
      sessionExpiresAtRef.current = res.data.sessionExpiresAt;
      warnedRef.current = false;
      modalHandleRef.current?.destroy();
      modalHandleRef.current = null;
    } catch (err) {
      // A 401 here means the BE already deleted the session (absolute cap exceeded, or
      // the upstream IdP rejected the refresh grant) — the cookie is already cleared.
      // Anything else is a transient error; the next tick or activity ping retries.
      if (extractApiError(err).status === 401) {
        redirectToLogin();
      }
    }
  }, [redirectToLogin]);

  const showExpiryWarning = useCallback(() => {
    if (warnedRef.current) return;
    warnedRef.current = true;
    modalHandleRef.current = Modal.warning({
      title: t('session.warningTitle'),
      content: t('session.warningBody'),
      okText: t('session.staySignedIn'),
      closable: false,
      keyboard: false,
      maskClosable: false,
      onOk: () => refresh(),
    });
  }, [t, refresh]);

  useEffect(() => {
    if (isDevLoginEnabled() || !seed) return;

    sessionExpiresAtRef.current = seed.sessionExpiresAt;
    let cancelled = false;
    let refreshIntervalId: number;

    const restartRefreshInterval = (periodMs: number) => {
      window.clearInterval(refreshIntervalId);
      refreshIntervalId = window.setInterval(() => {
        if (!cancelled) void refresh();
      }, periodMs);
    };

    const periodMs = (seed.refreshIntervalSeconds || DEFAULT_REFRESH_INTERVAL_SECONDS) * 1000;
    restartRefreshInterval(periodMs);

    // Upload/save/submit activity resets the cadence and refreshes right away, so an
    // active advisor never hits the interval boundary mid-action.
    const unsubscribe = subscribeToSessionActivity(() => {
      if (cancelled) return;
      restartRefreshInterval(periodMs);
      void refresh();
    });

    // Deliberately decoupled from the refresh interval above: a recent activity ping
    // restarts that timer, but the absolute deadline below is fixed and must still be
    // checked on its own cadence regardless of how recently the user was active.
    const deadlineCheckId = window.setInterval(() => {
      if (cancelled || sessionExpiresAtRef.current === null) return;
      const secondsLeft = sessionExpiresAtRef.current - Math.floor(Date.now() / 1000);
      if (secondsLeft <= 0) {
        redirectToLogin();
      } else if (secondsLeft <= WARNING_THRESHOLD_SECONDS) {
        showExpiryWarning();
      }
    }, DEADLINE_CHECK_MS);

    return () => {
      cancelled = true;
      window.clearInterval(refreshIntervalId);
      window.clearInterval(deadlineCheckId);
      unsubscribe();
    };
    // Intentionally re-runs only when the seed's own values change, not on every
    // refresh/showExpiryWarning identity change (those are stable-enough callbacks that
    // close over refs) — mirrors the eslint-disable already used for this kind of
    // effect in useEditorLock.ts / LogoutPage.tsx.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed?.sessionExpiresAt, seed?.refreshIntervalSeconds]);
}
